import { Grid } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import { deleteRequest } from '../../../../api/ServiceRequestedDetails';
import DocumentsOfRequestsCard from '../../../../components/DocumentsOfRequestsCard/DocumentsOfRequestsCard';
import { SweetAlert } from '../../../../components/SweetAlert/SweetAlert';
import TextInformation from '../../../../components/TextInformation/TextInformation';
import { HideGlobalLoading, ShowGlobalLoading } from '../../../../redux/actions/UiActions';
import { SmallHeightDivider } from '../../../../theme/Styles';
import COLORS from '../../../../theme/Colors';
import FONTS_FAMILY from '../../../../theme/FontsFamily';
import { filterDetailsData, filterDocuments, filterDocumentsInstitution } from '../../../../utilities/functions/ServiceRequestedDetailsUtil';
import { replaceGuionToSlashFromString } from '../../../../utilities/functions/StringUtil';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadFileWithName } from '../../../../utilities/functions/DownloadUtil';
import { Container } from '../../styles/ServiceRequestedDetailsStyles';

function Details() {
    const history = useHistory();
    const location = useLocation();

    // Obtener los query params
    const queryParams = new URLSearchParams(location.search);
    const docIdentification = queryParams.get('doc_identification');
    const statusID = queryParams.get('status_id');

    let { requestID } = useParams();
    const dispatch = useDispatch();
    const { citizenID } = useSelector((state) => state.authReducer);
    const queryClient = useQueryClient();

    const documentsRef = useRef(null);

    const requestData = queryClient.getQueryData(['serviceRequestedDetail', requestID]);

    const userData = queryClient.getQueryData(['userData']);

    const mutation = useMutation(deleteRequest);

    const isCompletedStatus = statusID == 7 || 
        String(requestData?.request?.status?.name || '').toLowerCase().includes('completad') || 
        String(requestData?.request?.status?.name || '').toLowerCase().includes('aprobad') ||
        String(requestData?.request?.status?.name || '').toLowerCase().includes('finalizad');

    const resolutionDoc = useMemo(() => {
        if (!requestData?.request || !isCompletedStatus) return null;
        const req = requestData.request;
        const instDoc = req.request_document?.find(d => 
            d.origin_doc === 1 || 
            String(d.name || '').toLowerCase().includes('resolu') ||
            String(d.name || '').toLowerCase().includes('licencia') ||
            String(d.name || '').toLowerCase().includes('certific')
        );
        const url = instDoc?.url || req.resolution_url || req.url_resolution || req.resolution_file || req.resolution_document;
        const name = instDoc?.name 
            ? `${instDoc.name}.${instDoc.extension || 'pdf'}` 
            : `Resolución_${req.approval_number || req.code || req.id}.pdf`;
        return { url: url || '#', name, approvalNumber: req.approval_number || req.code };
    }, [requestData?.request, isCompletedStatus]);

    const documentsData = useMemo(() => filterDocuments(requestData?.request?.request_document), [requestData?.request?.request_document]);
    const documentsInstitutionData = useMemo(() => {
        let list = filterDocumentsInstitution(requestData?.request?.request_document) || [];
        if (list.length === 0 && isCompletedStatus && resolutionDoc) {
            list = [{
                id: 'res-doc-institution',
                name: resolutionDoc.name,
                documentType: 'pdf',
                date: requestData?.request?.created_at 
                    ? new Date(replaceGuionToSlashFromString(requestData.request.created_at)).toLocaleDateString('es-DO') 
                    : new Date().toLocaleDateString('es-DO'),
                url: resolutionDoc.url,
                type: 'pdf'
            }];
        }
        return list;
    }, [requestData?.request, isCompletedStatus, resolutionDoc]);
    const detailData = useMemo(() => filterDetailsData(requestData?.data), [requestData?.data]);

    const handleChangeReportStatus = () => {
        SweetAlert.fire({
            icon: 'warning',
            title: 'Esta seguro de eliminar esta solicitud?',
            text: 'Una vez realizada esta acción no podra revertirse',
            showDenyButton: true,
            confirmButtonText: 'Confirmar',
            denyButtonText: `Cancelar`
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteRequest();
            }
        });
    };

    const handleDeleteRequest = () => {
        dispatch(ShowGlobalLoading('Cargando'));
        mutation.mutate(
            {
                citizen_id: userData?.payload?.citizen_id,
                request_id: requestID
            },
            {
                onSuccess: () => {
                    queryClient.removeQueries(['serviceRequestedDetail', requestID], { exact: true });
                    queryClient.invalidateQueries(['requestsList']);
                    history.push(`/app/mi-escritorio`);
                },
                onSettled: () => {
                    dispatch(HideGlobalLoading());
                }
            }
        );
    };

    useEffect(() => {
        if (window.location.hash === '#documents') {
            //When documents action required its send scroll to documents and remove hash
            documentsRef?.current?.scrollIntoView();
        }
    }, [window.location.hash]);

    // Styles
    const sectionCardStyle = {
        background: '#ffffff',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: 'none',
    };

    const sectionTitleStyle = {
        margin: '0 0 20px 0',
        fontSize: '17px',
        fontWeight: 700,
        color: COLORS.secondary,
        fontFamily: FONTS_FAMILY.bold.body,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    };

    const detailItemStyle = {
        padding: '14px 18px',
        background: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #f1f5f9',
        minHeight: '60px',
    };

    const detailLabelStyle = {
        margin: 0,
        fontSize: '11px',
        fontWeight: 600,
        color: '#94a3b8',
        fontFamily: FONTS_FAMILY.bold.body,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: '4px',
    };

    const detailValueStyle = {
        margin: 0,
        fontSize: '15px',
        fontWeight: 500,
        color: COLORS.secondary,
        fontFamily: FONTS_FAMILY.regular.body,
        wordBreak: 'break-word',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* Details Section */}
            <div style={sectionCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <h3 style={sectionTitleStyle}>
                        <span style={{ fontSize: '20px' }}>📋</span>
                        Detalles de Solicitud
                    </h3>
                    {(() => {
                        const hasPayment = Boolean(requestData?.request?.payment);
                        const hasActions = Boolean(requestData?.requiredActions && requestData.requiredActions.length > 0);
                        const isDeletableDraft = requestData?.request?.provisional === 1 && !hasPayment && !hasActions;

                        if (docIdentification == citizenID && statusID != 10 && statusID != 8 && statusID != 7 && isDeletableDraft) {
                            return (
                                <button
                                    onClick={handleChangeReportStatus}
                                    style={{
                                        padding: '8px 20px',
                                        borderRadius: '8px',
                                        border: '1px solid #fca5a5',
                                        background: '#fef2f2',
                                        color: '#dc2626',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        fontFamily: FONTS_FAMILY.bold.body,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    🗑️ Eliminar Solicitud
                                </button>
                            );
                        }
                        return null;
                    })()}
                </div>

                <Grid
                    container
                    spacing={2}
                >
                    {requestData?.request?.provisional === 0 && (
                        <Grid item xs={12} sm={6} md={4}>
                            <div style={detailItemStyle}>
                                <p style={detailLabelStyle}>Número de solicitud</p>
                                <p style={detailValueStyle}>{requestData?.request?.code}</p>
                            </div>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4}>
                        <div style={detailItemStyle}>
                            <p style={detailLabelStyle}>Estatus</p>
                            <p style={{
                                ...detailValueStyle,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                <span style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: statusID == 7 ? '#22c55e' : statusID == 8 ? '#ef4444' : statusID == 10 ? '#9ca3af' : '#3b82f6',
                                    display: 'inline-block',
                                    flexShrink: 0,
                                }}></span>
                                {requestData?.request?.status?.name}
                            </p>
                        </div>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <div style={detailItemStyle}>
                            <p style={detailLabelStyle}>Fecha</p>
                            <p style={detailValueStyle}>
                                {new Date(replaceGuionToSlashFromString(requestData?.request?.created_at)).toLocaleDateString('es-DO', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>
                    </Grid>

                    {detailData.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <div style={detailItemStyle}>
                                <p style={detailLabelStyle}>{item.label}</p>
                                <p style={detailValueStyle}>{item.value}</p>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </div>

            {/* Documents Emitted by Institution */}
            {documentsInstitutionData?.length > 0 && (
                <div style={sectionCardStyle}>
                    <h3 style={sectionTitleStyle}>
                        <span style={{ fontSize: '20px' }}>🏛️</span>
                        Documentos emitidos por institución
                    </h3>
                    <DocumentsOfRequestsCard data={documentsInstitutionData} />
                </div>
            )}

            {/* Uploaded Documents */}
            {documentsData?.length > 0 && (
                <div
                    ref={documentsRef}
                    name="documents"
                    style={sectionCardStyle}
                >
                    <h3 style={sectionTitleStyle}>
                        <span style={{ fontSize: '20px' }}>📎</span>
                        Documentos subidos
                    </h3>
                    <DocumentsOfRequestsCard data={documentsData} />
                </div>
            )}

            {/* Progress Timeline */}
            {requestData?.request?.progress !== undefined && (
                <div style={sectionCardStyle}>
                    <h3 style={sectionTitleStyle}>
                        <span style={{ fontSize: '20px' }}>📊</span>
                        Progreso de la solicitud
                    </h3>
                    <div style={{
                        width: '100%',
                        height: '10px',
                        backgroundColor: '#f1f5f9',
                        borderRadius: '10px',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${statusID == 8 || statusID == 10 ? 100 : requestData.request.progress}%`,
                            height: '100%',
                            background: statusID == 8 ? '#dc2626' :
                                        statusID == 10 ? '#6b7280' :
                                        statusID == 7 ? '#16a34a' :
                                        COLORS.secondary,
                            borderRadius: '10px',
                            transition: 'width 0.8s ease-in-out',
                        }}></div>
                    </div>
                    <p style={{
                        margin: '8px 0 0',
                        fontSize: '13px',
                        color: '#64748b',
                        fontFamily: FONTS_FAMILY.regular.body,
                        textAlign: 'right',
                    }}>
                        {statusID == 8 || statusID == 10 ? '100' : requestData.request.progress}% completado
                    </p>
                </div>
            )}
        </div>
    );
}

export default Details;
